import React from "react";
import LandingSection from "../components/LandingSection";
import { Table, Container, Row, Col, Card, Button } from "react-bootstrap";
import {
  ResponsiveContainer,
  PieChart,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import Footer from "../components/Footer";
import { Pie } from "react-chartjs-2";

// ---------- Helpers ----------
const fmtB = (n) => `${Number(n).toFixed(1)}B`;
const fmtPct = (n) => `${Number(n).toFixed(0)}%`;

// ---------- All Funds (FY 2026) ----------
const totalAllFunds = 30.8; // $B
const fundingSourcesAllFunds = [
  { name: "Federal Funds", amount: 8.3, pct: 27 },
  { name: "Income Tax Fund", amount: 6.6, pct: 21 },
  { name: "Other Funds", amount: 4.9, pct: 16 },
  { name: "General Fund", amount: 3.7, pct: 12 },
  { name: "Dedicated Credits", amount: 2.3, pct: 8 },
  { name: "Transportation Investment Fund", amount: 2.2, pct: 7 },
  { name: "Local Education Revenue", amount: 1.8, pct: 6 },
  { name: "Transportation Fund", amount: 0.9, pct: 3 },
];

// ✅ chartData
const chartData = {
  labels: fundingSourcesAllFunds.map((r) => r.name),
  datasets: [
    {
      label: "Funding Amount (in Billions)",
      data: fundingSourcesAllFunds.map((r) => r.amount),
      backgroundColor: [
        "#4f81bd", // Federal Funds (Blue)
        "#c0504d", // Income Tax Fund (Orange)
        "#9bbb59", // Other Funds (Green)
        "#8064a2", // General Fund (Purple)
        "#4bacc6", // Dedicated Credits (Light Blue)
        "#f79646", // Transportation Investment Fund (Gold)
        "#84a7d1", // Local Education Revenue (Pale Blue)
        "#d38582", // Transportation Fund (Red)
      ],
      borderColor: "#fff",
      borderWidth: 2,
      hoverOffset: 15,
    },
  ],
};

// ✅ Corrected chart options
const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        color: "#111",
        font: { size: 12 },
      },
    },
    title: {
      display: true,
      text: "Utah Budget 2025–2026 — Funding Sources (in Billions)",
      color: "#111",
      font: {
        size: 16,
        weight: "bold",
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const label = context.label || "";
          const value = context.parsed || 0;
          const total = context.chart._metasets[0].total || 0;
          const percent = ((value / total) * 100).toFixed(1);
          return `${label}: ${value.toFixed(2)}B (${percent}%)`;
        },
      },
    },
  },
};

const expenditureAreasAllFunds = [
  { name: "Social Services", amount: 10.4, pct: 34 },
  { name: "Public Education", amount: 8.6, pct: 28 },
  { name: "Transportation", amount: 4.0, pct: 13 },
  { name: "Higher Education", amount: 3.2, pct: 10 },
  { name: "General Government", amount: 2.6, pct: 8 },
  { name: "Law Enforcement", amount: 1.5, pct: 5 },
  { name: "Buildings & Debt", amount: 0.6, pct: 2 },
];

// ✅ chartData
const expChartData = {
  labels: expenditureAreasAllFunds.map((r) => r.name),
  datasets: [
    {
      label: "Funding Amount (in Billions)",
      data: expenditureAreasAllFunds.map((r) => r.amount),
      backgroundColor: [
        "#f4cccc", // Social Services (Light Pink)
        "#9fc5e8", // Public Education (Light Blue)
        "#eb8582", // Transportation (Red/Orange)
        "#b4a7d6", // Higher Education (Purple)
        "#a2c4c9", // General Government (Teal Blue)
        "#93c47d", // Law Enforcement (Green)
        "#7f6000", // Buildings & Debt (Brown)
      ],
      borderColor: "#fff",
      borderWidth: 2,
      hoverOffset: 15,
    },
  ],
};

// ✅ Corrected chart options
const expOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        color: "#111",
        font: { size: 12 },
      },
    },
    title: {
      display: true,
      text: "Utah Budget by Expenditure Areas All Funds",
      color: "#111",
      font: {
        size: 16,
        weight: "bold",
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const label = context.label || "";
          const value = context.parsed || 0;
          const total = context.chart._metasets[0].total || 0;
          const percent = ((value / total) * 100).toFixed(1);
          return `${label}: ${value.toFixed(2)}B (${percent}%)`;
        },
      },
    },
  },
};

// ---------- State Funds subset (GF/ITF/USF) ----------
const totalStateFunds = 11.8; // $B
const stateFundsSources = [
  { name: "Uniform School Fund (USF)", amount: 5.2, pct: 44 },
  { name: "General Fund (GF)", amount: 4.0, pct: 34 },
  { name: "Income Tax Fund (ITF)", amount: 2.6, pct: 22 },
];

const stateFundsExpenditures = [
  { name: "Public Education", amount: 5.9, pct: 50 },
  { name: "Higher Education", amount: 2.0, pct: 17 },
  { name: "Social Services", amount: 1.8, pct: 15 },
  { name: "Law Enforcement", amount: 1.0, pct: 9 },
  { name: "General Government", amount: 0.7, pct: 6 },
  { name: "Other/Capital", amount: 0.3, pct: 3 },
  { name: "Debt Service", amount: 0.03, pct: 0 },
  { name: "Transportation", amount: 0.01, pct: 0 },
];

// ---------- Social Services split (All Funds, FY 2026) ----------
const socialServicesSplit = [
  {
    dept: "Dept. of Health & Human Services (DHHS)",
    amount: 7080.2925,
    pct: 81.2,
  },
  { dept: "Dept. of Workforce Services (DWS)", amount: 1636.3516, pct: 18.8 },
];

// ---------- Highlights / Headlines ----------
const highlights = [
  {
    label: "Total (All Funds) - FY 2026",
    value: fmtB(totalAllFunds),
    color: "bg-teal-100",
  },
  {
    label: "State Funds (GF/ITF/USF) - FY 2026",
    value: fmtB(totalStateFunds),
    color: "bg-red-100",
  },
  // {
  //   label: "WPU Value - FY 2026",
  //   value: "$4,674 (↑4.0%)",
  //   color: "bg-purple-100",
  // },
  // {
  //   label: "Rainy-Day Deposit - FY2025-FY2026 (one-time)",
  //   value: "$145.2M",
  //   color: "bg-green-100",
  // },
];

const COLORS = [
  "#6d9eeb",
  "#76679e",
  "#c0504d"
];

const EXP_COLORS = [
  "#9fc5e8",
  "#b4a7d6",
  "#f4cccc",
  "#93c47d",
  "#b4dde4",
  "#b45f06",
];

const UtahBudget = () => {
  return (
    <div>
      <LandingSection
        data={{
          imageName: "utah_budget.jpg",
          title: "Utah Budget",
        }}
      />

      <div className="verticals_section">
        <div className="verticals-intro">
          <div className="verticals-intro-sec1">
            <h2>Utah’s Budget: Building a Balanced Future</h2>
          </div>
          <div className="verticals-intro-sec2">
            <p>
              Utah’s state budget reflects a commitment to fiscal responsibility
              and long-term growth. Each year, the legislature allocates funds
              across key areas such as education, infrastructure, healthcare,
              and economic development, ensuring resources are managed
              efficiently while maintaining one of the nation’s strongest
              economies.
            </p>
          </div>
        </div>
      </div>

      <div className="min-h-screen w-full bg-[#fff] text-black">
        <header className="max-w-7xl mx-auto px-4 pt-10 pb-6">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            Utah Budget — FY 2025–2026
          </h1>
          <p className="mt-2 text-sm md:text-base opacity-80">
            A quick, visual summary of funding sources, spending by area, and
            key highlights.
          </p>
        </header>

        {/* KPI Cards */}
        <section className="max-w-7xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-center">
          {highlights.map((h, i) => (
            <div
              key={i}
              className={`rounded-2xl ${h.color} backdrop-blur border border-white/10 p-4 shadow flex flex-col justify-between`}
            >
              <div className="text-xs uppercase tracking-wider opacity-70">
                {h.label}
              </div>
              <div className="mt-1 text-2xl font-bold">{h.value}</div>
            </div>
          ))}

          <a
            href="https://le.utah.gov/interim/2025/pdf/00002339.pdf"
            target="_blank"
            className="text-right"
          >
            Data Source
          </a>
        </section>

        <section className="max-w-7xl mx-auto px-4 mt-10">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4 shadow">
            <h2 className="text-xl font-semibold">
              All Funds — By Funding Source (FY 2026)
            </h2>
            <p className="text-sm opacity-75 mb-3">
              Total: {fmtB(totalAllFunds)}; shares shown as percent of total
              funds.
            </p>
            <Row className="gy-4 flex items-center">
              {/* Table */}
              <Col md={6}>
                <div>
                  <table className="w-full border-collapse shadow-md rounded-lg overflow-hidden">
                    <thead className="bg-blue-700 text-white">
                      <tr>
                        <th className="p-2 text-left">Funding Source</th>
                        <th className="p-2 text-right">Amount (Billion)</th>
                        <th className="p-2 text-right">Percentage of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fundingSourcesAllFunds.map((r) => (
                        <tr
                          key={r.name}
                          className="odd:bg-gray-100 even:bg-white hover:bg-gray-100 transition"
                        >
                          <td className="p-2 text-left">{r.name}</td>
                          <td className="p-2 text-right">${r.amount}</td>
                          <td className="p-2 text-right">{r.pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Col>

              {/* Chart */}
              <Col md={6}>
                <div className="p-3 bg-white rounded-3 shadow-sm">
                  <div
                    style={{
                      height: "400px",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Pie data={chartData} options={options} />
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 mt-10">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4 shadow">
            <h2 className="text-xl font-semibold">
              All Funds — By Expenditure Area (FY 2026)
            </h2>
            <p className="text-sm opacity-75 mb-3">
              Total: {fmtB(totalAllFunds)}; shares shown as percent of total
              funds.
            </p>
            <Row className="gy-4 flex items-center">
              {/* Table */}
              <Col md={6}>
                <div>
                  <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                    <thead className="bg-blue-700 text-white">
                      <tr>
                        <th className="p-2 text-left">Expenditure Area</th>
                        <th className="p-2 text-right">Amount (Billion)</th>
                        <th className="p-2 text-right">Percentage of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenditureAreasAllFunds.map((r, i) => (
                        <tr
                          key={r.name}
                          className="odd:bg-gray-100 even:bg-white hover:bg-gray-100 transition"
                        >
                          <td className="p-2 text-left">{r.name}</td>
                          <td className="p-2 text-right">${r.amount}</td>
                          <td className="p-2 text-right">{r.pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Col>

              {/* Chart */}
              <Col md={6}>
                <div className="p-3 bg-white rounded-3 shadow-sm">
                  <div
                    style={{
                      height: "400px",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Pie data={expChartData} options={expOptions} />
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 mt-10">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4 shadow">
            <h2 className="text-xl font-semibold">
              State Funds — Sources (GF/ITF/USF)
            </h2>
            <p className="text-sm opacity-75 mb-3">
              Total: {fmtB(totalStateFunds)}
            </p>
            <Row className="gy-4 flex items-center">
              {/* Table */}
              <Col md={6}>
                <div>
                  <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                    <thead className="bg-blue-700 text-white">
                      <tr>
                        <th className="p-2 text-left">Funding Source</th>
                        <th className="p-2 text-right">Amount (Billion)</th>
                        <th className="p-2 text-right">Percentage of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stateFundsSources.map((r, i) => (
                        <tr
                          key={r.name}
                          className="odd:bg-gray-100 even:bg-white hover:bg-gray-100 transition"
                        >
                          <td className="p-2 text-left">{r.name}</td>
                          <td className="p-2 text-right">${r.amount}</td>
                          <td className="p-2 text-right">{r.pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Col>

              {/* Chart */}
              <Col md={6}>
                <div className="p-3 bg-white rounded-3 shadow-sm h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={stateFundsSources}
                      margin={{ top: 16, right: 24, bottom: 8, left: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 12 }}
                        interval={0}
                      />
                      <YAxis
                        label={{
                          value: "Billions ($)",
                          angle: -90,
                          position: "insideLeft",
                          offset: 10,
                        }}
                      />
                      <Tooltip
                        formatter={(v, n, p) => {
                          const pct = p?.payload?.pct
                            ? fmtPct(p.payload.pct)
                            : "";
                          return [`${fmtB(v)} ${pct && "• " + pct}`, n];
                        }}
                      />
                      <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                        {stateFundsSources.map((_, idx) => (
                          <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Col>
            </Row>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 mt-10">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4 shadow">
            <h2 className="text-xl font-semibold">
              State Funds — Expenditures
            </h2>
            <p className="text-sm opacity-75 mb-3">
              Total: {fmtB(totalStateFunds)}
            </p>
            <Row className="gy-4 flex items-center">
              {/* Table */}
              <Col md={6}>
                <div>
                  <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                    <thead className="bg-blue-700 text-white">
                      <tr>
                        <th className="p-2 text-left">Expenditure Area</th>
                        <th className="p-2 text-right">Amount (Billion)</th>
                        <th className="p-2 text-right">Percentage of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stateFundsExpenditures.map((r, i) => (
                        <tr
                          key={r.name}
                          className="odd:bg-gray-100 even:bg-white hover:bg-gray-100 transition"
                        >
                          <td className="p-2 text-left">{r.name}</td>
                          <td className="p-2 text-right">${r.amount}</td>
                          <td className="p-2 text-right">{r.pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Col>

              {/* Chart */}
              <Col md={6}>
                <div className="p-3 bg-white rounded-3 shadow-sm h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={stateFundsExpenditures}
                      layout="vertical" // 👈 makes it horizontal
                      margin={{ top: 16, right: 24, bottom: 8, left: 50 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />

                      {/* Y-axis now holds category labels */}
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={120}
                        tick={{ fontSize: 12 }}
                      />

                      {/* X-axis now holds numeric values */}
                      <XAxis
                        type="number"
                        label={{
                          value: "Billions ($)",
                          position: "insideBottom",
                          offset: -5,
                        }}
                      />

                      <Tooltip
                        formatter={(v, n, p) => [
                          `${fmtB(v)} • ${fmtPct(p.payload.pct)}`,
                          n,
                        ]}
                      />

                      <Bar dataKey="amount" radius={[0, 6, 6, 0]}>
                        {stateFundsExpenditures.map((_, idx) => (
                          <Cell
                            key={idx}
                            fill={EXP_COLORS[idx % EXP_COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Col>
            </Row>
          </div>
        </section>

        {/* Social Services Split */}
        <section className="max-w-7xl mx-auto px-4 mt-10">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4 shadow">
            <h2 className="text-xl font-semibold">
              Social Services — FY 2026 (All Funds)
            </h2>
            <p className="text-sm opacity-75 mb-3">
              Department shares within Social Services total.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left opacity-80">
                    <th className="py-2 pr-4">Department</th>
                    <th className="py-2 pr-4">Amount ($)</th>
                    <th className="py-2 pr-4">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {socialServicesSplit.map((r, i) => (
                    <tr key={i} className="border-t border-white/10">
                      <td className="py-2 pr-4">{r.dept}</td>
                      <td className="py-2 pr-4">
                        {r.amount.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td className="py-2 pr-4">{fmtPct(r.pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Callouts */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <Callout
                title="Medicaid Growth & Inflation"
                ongoing="$82.0M"
                onetime="$85.2M"
                className="bg-gradient-to-r from-blue-100 to-purple-100 p-5 rounded-xl shadow-md"
              />
              <Callout
                title="DSPD Waiting List Relief"
                ongoing="$13.5M"
                onetime="$4.7M"
              />
              <Callout title="Nursing Home Reimbursement" ongoing="$2.5M" />
              <Callout
                title="Childcare & Tax Credits"
                ongoing="$2.0M + $4.1M + $24.1M"
                onetime="$1.3M"
              />
            </div>
          </div>
        </section>

        {/* Notes */}
        <footer className="max-w-7xl mx-auto px-4 py-10 opacity-80 text-xs">
          <p>
            Revenue Estimates (State Funds): FY25 GF $4.20B → FY26 $3.90B; FY25
            ITF $7.40B → FY26 $7.60B. Total Funds Spending FY26: $30.8B
            (↑$924.6M, 3.1%); GF/ITF/USF FY26: $11.8B (↓$818.5M, −6.5%).
          </p>
        </footer>
      </div>
      <Footer />
    </div>
  );
};

function Callout({ title, ongoing, onetime }) {
  return (
    <div
      className="rounded-xl bg-white/5 border border-white/10 p-3"
      style={{ backgroundColor: "#f9fafb" }}
    >
      <div className="text-sm font-semibold">{title}</div>
      <div className="text-xs opacity-80">
        {ongoing && <div>Ongoing: {ongoing}</div>}
        {onetime && <div>One-time: {onetime}</div>}
      </div>
    </div>
  );
}

export default UtahBudget;
